import React, { useEffect, useRef, useState } from 'react';
import ImageLightbox from '../ImageLightbox';
import ContentTable from './ContentTable';
import SafeRichContent from './SafeRichContent';
import { getGalleryImages } from './contentLayout';
import { getAnchoredTableIds } from './bookTableAnchor';
import './ContentDisplay.css';

const ContentDisplay = ({ selectedItem, bookData, contentLanguage = 'fr' }) => {
  const contentRef = useRef(null);
  const [lightbox, setLightbox] = useState({ open: false, src: '', alt: '', caption: '' });

  // Effet pour défiler vers le titre de l'élément sélectionné
  useEffect(() => {
    if (selectedItem && contentRef.current) {
      setTimeout(() => {
        // Calculer la position du header fixe pour ajuster le défilement
        const header = document.querySelector('[class*="sticky top-20"]');
        const headerHeight = header ? header.offsetHeight : 80;
        
        // Position du titre par rapport au haut de la page
        const titleElement = contentRef.current.querySelector('.content-title');
        if (titleElement) {
          const titlePosition = titleElement.getBoundingClientRect().top + window.pageYOffset;
          
          // Défiler vers le titre avec un décalage pour le header
          window.scrollTo({
            top: titlePosition - headerHeight - 20, // 20px de marge supplémentaire
            behavior: 'smooth'
          });
        }
      }, 150);
    }
  }, [selectedItem]);

  if (!selectedItem) {
    return (
      <div className="content-display">
        <div className="empty-state">
          <div className="empty-icon">📄</div>
          <h3>Sélectionnez un élément dans le sommaire</h3>
          <p>Cliquez sur un chapitre, une section ou une sous-section pour afficher son contenu ici.</p>
        </div>
      </div>
    );
  }

  const { type, data } = selectedItem;
  
  // Vérifier si les données existent
  if (!data) {
    return (
      <div className="content-display">
        <div className="empty-state">
          <div className="empty-icon">⚠️</div>
          <h3>Données non disponibles</h3>
          <p>Les données pour cet élément ne sont pas disponibles.</p>
        </div>
      </div>
    );
  }

  const galleryImages = getGalleryImages(data.images, contentLanguage);
  const anchoredTableIds = getAnchoredTableIds(data.content, data.tables);
  const legacyTables = (Array.isArray(data.tables) ? data.tables : []).filter(
    (table) => !anchoredTableIds.has(table?.id)
  );

  const renderContent = () => {
    if (!data.content || typeof data.content !== 'string' || data.content.trim() === '') {
      return (
        <div className="no-content">
          <p>Cet élément ne contient pas de texte.</p>
        </div>
      );
    }

    return (
      <SafeRichContent
        content={data.content}
        images={data.images}
        contentLanguage={contentLanguage}
        tables={data.tables}
        className="content-text select-text [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6"
        onImageClick={(image) => {
          if (!image?.url) return;
          setLightbox({
            open: true,
            src: image.url,
            alt: image.caption || 'Image',
            caption: image.caption || 'Image',
          });
        }}
      />
    );
  };

  const isIntroChapter = (chapter) => {
    if (!chapter) return false;
    return !!chapter.is_intro;
  };

  const getDisplayChapterNumber = (chapter) => {
    if (!chapter) return 0;
    if (!bookData?.chapters) return chapter?.order ?? 0;
    const countIntroBefore = bookData.chapters.filter(c => (c.order < chapter.order) && isIntroChapter(c)).length;
    return (chapter?.order ?? 0) - countIntroBefore;
  };

  const getBreadcrumb = () => {
    const breadcrumb = [];
    
    if (selectedItem.type === 'chapter') {
      if (data && data.order !== undefined && data.title) {
        if (isIntroChapter(data)) {
          breadcrumb.push(`${data.title}`);
        } else {
          breadcrumb.push(`Chapitre ${getDisplayChapterNumber(data)}: ${data.title}`);
        }
      }
    } else if (selectedItem.type === 'section') {
      const chapter = selectedItem.chapterData;
      if (chapter && chapter.order !== undefined && chapter.title) {
        if (isIntroChapter(chapter)) {
          breadcrumb.push(`${chapter.title}`);
        } else {
          breadcrumb.push(`Chapitre ${getDisplayChapterNumber(chapter)}: ${chapter.title}`);
        }
      }
      if (data && data.order !== undefined && data.title) {
        if (chapter && isIntroChapter(chapter)) {
          breadcrumb.push(`Section ${data.order}: ${data.title}`);
        } else {
          breadcrumb.push(`Section ${getDisplayChapterNumber(chapter)}.${data.order}: ${data.title}`);
        }
      }
    } else if (selectedItem.type === 'subsection') {
      const chapter = selectedItem.chapterData;
      const section = selectedItem.sectionData;
      if (chapter && chapter.order !== undefined && chapter.title) {
        if (isIntroChapter(chapter)) {
          breadcrumb.push(`${chapter.title}`);
        } else {
          breadcrumb.push(`Chapitre ${getDisplayChapterNumber(chapter)}: ${chapter.title}`);
        }
      }
      if (section && section.order !== undefined && section.title) {
        if (chapter && isIntroChapter(chapter)) {
          breadcrumb.push(`Section ${section.order}: ${section.title}`);
        } else {
          breadcrumb.push(`Section ${getDisplayChapterNumber(chapter)}.${section.order}: ${section.title}`);
        }
      }
      if (data && data.order !== undefined && data.title) {
        if (selectedItem.numbering) {
          breadcrumb.push(`Sous-section ${selectedItem.numbering}: ${data.title}`);
        } else if (chapter && isIntroChapter(chapter)) {
          breadcrumb.push(`Sous-section ${section?.order}.${data.order}: ${data.title}`);
        } else {
          breadcrumb.push(`Sous-section ${getDisplayChapterNumber(chapter)}.${section?.order}.${data.order}: ${data.title}`);
        }
      }
    }
    
    return breadcrumb;
  };

  const getTypeIcon = () => {
    switch (type) {
      case 'chapter':
        return '📚';
      case 'section':
        return '📑';
      case 'subsection':
        return '📄';
      default:
        return '📄';
    }
  };

  return (
    <div className="content-display" ref={contentRef}>
      <ImageLightbox
        open={lightbox.open}
        src={lightbox.src}
        alt={lightbox.alt}
        caption={lightbox.caption}
        onClose={() => setLightbox({ open: false, src: '', alt: '', caption: '' })}
      />
      <div className="content-header">
        <div className="breadcrumb">
          {getBreadcrumb().map((item, index) => (
            <React.Fragment key={index}>
              <span className="breadcrumb-item">{item}</span>
              {index < getBreadcrumb().length - 1 && (
                <span className="breadcrumb-separator">›</span>
              )}
            </React.Fragment>
          ))}
        </div>
        
        <div className="content-title">
          <span className="type-icon">{getTypeIcon()}</span>
          <h1>{data.title || 'Titre non disponible'}</h1>
          <span className="content-number">(ID: {data.id || 'N/A'})</span>
        </div>
      </div>

      <div className="content-body">
        <div className="content-text">
          {renderContent()}
        </div>

        {/* Afficher les images si elles existent */}
        {galleryImages.length > 0 && (
          <div className="content-images">
            <h3>Images</h3>
            <div className="images-grid">
              {galleryImages.map((image, index) => {
                const url = typeof image === 'string' ? image : image?.url || '';
                const caption =
                  typeof image === 'string'
                    ? `Image ${index + 1}`
                    : image?.caption || `Illustration ${index + 1}`;
                return (
                <div key={index} className="image-item">
                  <img
                    src={url}
                    alt={caption}
                    className="content-image"
                    style={{ cursor: 'zoom-in' }}
                    onClick={() => {
                      if (!url) return;
                      setLightbox({
                        open: true,
                        src: url,
                        alt: caption,
                        caption,
                      });
                    }}
                  />
                  <p className="image-caption">{caption}</p>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Afficher les tableaux s'ils existent */}
        {legacyTables.length > 0 && (
          <div className="content-tables">
            <h3>Tableaux</h3>
            {legacyTables.map((table, index) => (
              <div key={table?.id || index} className="table-item">
                {(() => {
                  // 1) Snapshot image table
                  const url = table?.url || '';
                  const isSnapshot = table?.is_snapshot === true || /\.(png|jpe?g|gif|webp)$/i.test(url);
                  if (isSnapshot && url) {
                    return (
                      <>
                        <div className="bg-white p-3 flex justify-center items-center border border-gray-200 rounded">
                          <img
                            src={url}
                            alt={table.title || table.caption || `Tableau ${index + 1}`}
                            className="max-w-full h-auto"
                            style={{ cursor: 'zoom-in' }}
                            onClick={() => {
                              if (!url) return;
                              const caption = table.caption || table.title || `Tableau ${index + 1}`;
                              setLightbox({ open: true, src: url, alt: caption, caption });
                            }}
                          />
                        </div>
                        <p className="table-caption">{table.caption || table.title || `Tableau ${index + 1}`}</p>
                      </>
                    );
                  }

                  return <ContentTable table={table} />;
                })()}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentDisplay;
